import { Geometry, HorizontalAlign, Log, LogGroup, Point, Rect, Size, VerticalAlign } from ".././MyHelpers";
import { AudioManager, Track } from "../AudioManager";
import { Resourceful } from "../ResourceManager/Resourceful";
import { ResourceIdentifier } from "../ResourceManager/ResourceIdentifier";
import { IReadOnlyResourceManager, IWriteOnlyResourceManager } from "../ResourceManager/ResourceManager";
import { SpriteResource } from "../ResourceManager/SpriteResource";
import { ZIndex } from "../ZIndex";
import { MyCanvas } from "./MyCanvas";
import { SlotFeature } from "./SlotFeature";
import { ReelSetMapper, ReelSetMapperOverlay } from "./SpinAnimation/ReelSetMapper";
import { CanvasSprite } from "./Sprite/CanvasSprite";
import { ISprite } from "./Sprite/ISprite";
import { IFeatureConfiguration, PlayWindow } from "../api";
import { IInterval } from "../Context/Interval";
import { ISystemContext } from "../Context/SystemContext";
import { ITimeout } from "../Context/Timeout";
import { Settings } from "../Settings";
import { ExpandingWildsBulkPIXIAPP } from "./ExpandingWildBulkPIXIAPP";
import { SlotSymbol } from "./SlotSymbol";
import { SymbolTypeAnim } from "./SpineSymbols/SymbolsCollection";

export interface ExpandingWildPosition
{
	readonly reel: number;
	readonly row: number;
}

export interface ExpandingWildBulkResult
{
	readonly Positions: ExpandingWildPosition[];
}

export interface ExpandingWildsBulkConfig extends IFeatureConfiguration
{
    readonly Symbol: string;
}


export enum WildDisplayDirection
{
	Right,
	Down,
	Left,
	Up
}


export interface ExpandingWildBulkParam
{
	readonly wildIndex: number;
	readonly logGroup: LogGroup;
	readonly expandingWildSymbolIndex: number;
	readonly sprite: CanvasSprite;
	readonly symbolsRect: Rect;
	readonly reelSetMapper: ReelSetMapper;

	readonly systemContext: ISystemContext;
}


export type ExpandingWildBulkOnShowCallback = (wildIndex: number) => void;
export class ExpandingWildBulk
{

	// size of the wild, in symbol unites
	public symbolsRect: Rect = null;

	// position of the original wild, that triggered expanding wild
	protected symbolsCenter: Point = null;

	private wildIndex: number
	protected logGroup: LogGroup;
	private expandingWildSymbolIndex: number;

	private reelSetMapper: ReelSetMapper = null;
    private reelSetMapper2: ReelSetMapper = null;
    private reelSetMapper3: ReelSetMapper = null;

	private sprite: CanvasSprite = null;

	protected systemContext: ISystemContext = null;
	protected firstSymbolDelay: ITimeout = null;
	protected nextSymbolDelay: IInterval = null;

	constructor(param: ExpandingWildBulkParam) {
		//wildIndex: number, logGroup: LogGroup, expandingWildSymbolIndex: number, sprite: CanvasSprite, symbolsRect: PixelRect, reelSetMapper: ReelSetMapper) {
		this.wildIndex = param.wildIndex;
		this.logGroup = param.logGroup.Add("ExpandingWildBulk");
		this.expandingWildSymbolIndex = param.expandingWildSymbolIndex;
		this.sprite = param.sprite;
		this.symbolsRect = param.symbolsRect;
		this.reelSetMapper = param.reelSetMapper;
		this.reelSetMapper2 = param.reelSetMapper;
		this.reelSetMapper3 = param.reelSetMapper;

		this.systemContext = param.systemContext.CreateSubContext(`wild-${this.wildIndex}`);

		
		this.sprite.View().style.position = "absolute";
		this.sprite.View().style.visibility = "hidden"; // default hidden

		this.firstSymbolDelay = this.systemContext.TimingContext().CreateTimeout("firstSymbolDelay", 250, () => {
			this.nextSymbolDelay.Start();
		});

		this.nextSymbolDelay = this.systemContext.TimingContext().CreateInterval("nextSymbolDelay", 185, () => {
			this.DisplayNext();
		});
	}

	public SetRect(symbolsRect: Rect, symbolsCenter: Point, pixelsRect: Rect): void {
		this.symbolsRect = symbolsRect;
		this.symbolsCenter = symbolsCenter;

		this.sprite.SetSize(pixelsRect.Size());
		this.sprite.View().style.left = pixelsRect.x + "px";
		this.sprite.View().style.top = pixelsRect.y + "px";
	}

	public View(): HTMLElement {
		return this.sprite.View();
	}

	protected currentDirection: WildDisplayDirection = WildDisplayDirection.Right;
	protected currentWild: Rect = new Rect(0, 0, 1, 1);
	//protected displayTimer: number = -1;
	//protected displayTimerDelay: number = -1;
	protected wildsDisplayed: number = 0;
	protected symbolsToDisplay: number = 0;

	protected DisplayNext(): void 
	{
		// display current wild
		this.reelSetMapper.AddOverlay(new ReelSetMapperOverlay(this.currentWild, this.expandingWildSymbolIndex));

		// Log.Debug(this.logGroup, "SEQUENTIAL WILDS - Displaying wild " + this.wildIndex + " - " + this.wildsDisplayed + " position: " + this.currentWild.x + ","+this.currentWild.y);

		// keep count how many we showed
		this.wildsDisplayed++;

		if (this.wildsDisplayed >= this.symbolsToDisplay)
		{
			this.nextSymbolDelay.Stop()
		}
		else
		{
			// advance to next one
			do
			{
				let newx: number = this.currentWild.x;
				let newy: number = this.currentWild.y;

				switch (this.currentDirection)
				{
					case WildDisplayDirection.Right:
						newx++;

						// if we reached right most symbol, go next symbol down
						if (newx +1 >= this.symbolsRect.x + this.symbolsRect.width)
						{
							this.currentDirection = WildDisplayDirection.Down;
						}

						break;

					case WildDisplayDirection.Down:
						newy++;

						// if we reached bottom most symbol, go next symbol left
						if (newy +1 >= this.symbolsRect.y + this.symbolsRect.height)
						{
							this.currentDirection = WildDisplayDirection.Left;
						}

						break;

					case WildDisplayDirection.Left:
						newx--;

						// if we reached left most symbol, go next symbol up
						if (newx  <= this.symbolsRect.x)
						{
							this.currentDirection = WildDisplayDirection.Up;
						}

						break;

					case WildDisplayDirection.Up:
						newy--;

						// if we reached top most symbol, go next symbol right
						if (newy  <= this.symbolsRect.y)
						{
							this.currentDirection = WildDisplayDirection.Right;
						}

						break;
				}

				this.currentWild = this.currentWild.SetPosition(newx, newy);

			}
			// skip center one (can happen only in 3x2, 2x3, 2x2, but not in 3x3)
			while (this.currentWild.x == this.symbolsCenter.x && this.currentWild.y == this.symbolsCenter.y);
		}
	}

	public Show(): void {

		// TODO zoom in wild
		this.sprite.View().style.visibility = "visible";
		this.sprite.Restart();


		//lets always start from left to right in top line
		this.currentDirection = WildDisplayDirection.Right;

		// display first wild right away
		this.wildsDisplayed = 0;
		this.currentWild = this.currentWild.SetPosition(this.symbolsRect.x, this.symbolsRect.y);

		// top left corner starts with original wild, skip it right away
		if (this.currentWild.x == this.symbolsCenter.x &&
			this.currentWild.y == this.symbolsCenter.y)
		{
			this.currentWild = this.currentWild.Move(1, 0);
			this.currentDirection = WildDisplayDirection.Down;
		}

		//this.DisplayNext();

		// did we display all symbols?
		// 3x3 = 9 - center symbol = 8
		// 2x3 = 6 - center symbol = 5
		// 2x2 = 4 - center symbol = 3
		this.symbolsToDisplay = this.symbolsRect.width * this.symbolsRect.height -1;

		// display next wilds in regular interval
		this.firstSymbolDelay.Start();

		this.OnShow();
	}

	public Hide(): void {
		
		// apply on hide to prevent loosing wilds
		this.Apply();

		this.sprite.View().style.visibility = "hidden";
		this.sprite.Stop();

		this.firstSymbolDelay.Stop();
		this.nextSymbolDelay.Stop();
	}

	public Update(): void {
		this.sprite.Update();
	}

	public Apply(): void
	{
		this.reelSetMapper.AddOverlay(new ReelSetMapperOverlay(this.symbolsRect, this.expandingWildSymbolIndex));
	}


	private onShowCallbacks: ExpandingWildBulkOnShowCallback[] = [];
	public AddOnShowCallback(callback: ExpandingWildBulkOnShowCallback) {
		this.onShowCallbacks.push(callback);
	}

	private OnShow() {
		for (let i = 0 ; i < this.onShowCallbacks.length; i++) {
			this.onShowCallbacks[i](this.wildIndex);
		}
	}
}



export type ExpandingWildsBulkFinishedCallback = () => void;
export type ExpandingWildsBulkNextWildCallback = (wildIndex: number, rect: Rect) => void;

export class ExpandingWildsBulk extends SlotFeature implements Resourceful
{
	protected params: ExpandingWildsBulkParam = null;
	// size of slot window, in pixels
	private slotWindow: Rect = null;

	// size of slot window, in symbols
	private slotWindowSymbolSize: PlayWindow = null;

	// size of single symbol, in pixels
	private symbolSize: Size = null;

	private sprite: ISprite = null;
	private canvas: MyCanvas = null;
	private view: HTMLElement = null;

	private currentWild: number = -1;
	//private timer: number = 0;

	//private logGroup: string = "ExpandingWildsBulk";

	private reelSetMapper: ReelSetMapper = null;


    private symbol: SlotSymbol = null;
	private audio: AudioManager = null;
	private systemContext: ISystemContext;
	private nextWildDelay: IInterval;

	public constructor(param: ExpandingWildsBulkParam, config: ExpandingWildsBulkConfig)
	{
		super(param.logGroup, param.resid, "ExpandingWildsBulk", param.resourcesBasePath);

		this.params = param;
		this.symbol = param.availableSymbols.find(s => s.ID == config.Symbol);
		
        if (!this.symbol)
		    throw `No required symbol ${config.Symbol}`;
		
		this.slotWindow = param.slotWindow;
		this.slotWindowSymbolSize = param.slotWindowSymbolSize;
		this.reelSetMapper = param.reelSetMapper;

		if (this.reelSetMapper == null) {
			Log.Error(this.logGroup, "ReelSetMapper should not be null");
			debugger;
		}

		this.symbolSize = new Size(this.slotWindow.width / this.slotWindowSymbolSize.Width, this.slotWindow.height / this.slotWindowSymbolSize.Height);

		this.audio = param.audio;

		this.view = document.createElement("div");
		this.view.classList.add(this.resid.Scope());
		//this.view.style.zIndex=ZIndex.WildBulk.toString();

		this.systemContext = param.systemContext.CreateSubContext("ExpandingWildsBulk");

		this.nextWildDelay = this.systemContext.TimingContext().CreateInterval("nextWildDelay", 4000, () => {
			this.NextWild();
		});
	}

	public wilds: ExpandingWildBulk[] = [];
	private lastResult: ExpandingWildBulkResult = null;

	public async PrepareResources(resourceManager: IWriteOnlyResourceManager): Promise<void> {
		// load sprite
		resourceManager.AddGlobal(new SpriteResource(this.resourcesBasePath + "expanding_wild_bulk", ResourceIdentifier.Global.Add(this.params.gameName).Add("Feature").Add("ExpandingWildBulk"), true, this.logGroup.Add("ExpandingWildBulk"), true));

		// load sound
		this.audio.AddTrack(new Track(this.resourcesBasePath + "background" + Settings.audioFormat, this.resid.ScopedResourceName("background")));		
	}

	public async ResourcesLoaded(resourceManager: IReadOnlyResourceManager): Promise<void> {

		this.sprite = resourceManager.Get<SpriteResource>(ResourceIdentifier.Global.Add(this.params.gameName).Add("Feature").Add("ExpandingWildBulk")).sprite;
	}

	public Prepare(featureResult: any): void
	{
		let result: ExpandingWildBulkResult = featureResult as ExpandingWildBulkResult;
		this.lastResult = result;

		// create additional wilds
		if (this.wilds.length < result.Positions.length)
		{
			for (let i = this.wilds.length; i < result.Positions.length; i++) {


				let sprite: CanvasSprite = new CanvasSprite({sprite: this.sprite.Copy(), canvasSize: new Size(1, 1)});

				let param: ExpandingWildBulkParam = {
                    wildIndex: i, 
                    logGroup: this.logGroup,
                    expandingWildSymbolIndex: this.symbol.Index,
                    sprite: sprite, 
                    symbolsRect: new Rect(0, 0, 1, 1),
                    reelSetMapper: this.reelSetMapper,
					systemContext: this.systemContext,
                };
				sprite = null;

				let wild: ExpandingWildBulk = new ExpandingWildBulk(param);
				wild.View().style.zIndex=ZIndex.WildBulk.toString();
				wild.AddOnShowCallback((wildIndex: number) => this.OnWildShown(wildIndex));

				this.wilds.push(wild);
				this.view.appendChild(wild.View());
				wild = null;
			}
		}

		// wild expands to all neighbours
		for (let i = 0; i < result.Positions.length; i++) {
			let position: ExpandingWildPosition = result.Positions[i];

			let leftReel = Math.max(position.reel -1, 0);
	        let rightReel = Math.min(position.reel + 1, this.slotWindowSymbolSize.Width - 1);

	        
	        let topSymbol = Math.max(position.row - 1, 0);
	        let bottomSymbol = Math.min(position.row + 1, this.slotWindowSymbolSize.Height - 1);

			// include right and bottom symbols, they are covered
			let wildSymbolsRect = Rect.FromCoordiantes(leftReel, topSymbol, rightReel +1, bottomSymbol +1);		

			// calculate actual size, so that image covers the symbols that will change

			// remember center, wild zooms in from the center symbol, that triggered
			let centerSymbol = new Point(position.reel, position.row);
			// center, in pixels
			
			//let center = new Point((centerSymbol.x + 0.5) * this.symbolSize.width, (centerSymbol.y + 0.5) * this.symbolSize.height);



			let wild: ExpandingWildBulk = this.wilds[i];

			// calculate size of canvas, so that it covers whole area 
			let wildContainRect: Rect = new Rect(
				(position.reel * this.symbolSize.Width) + this.slotWindow.x - (this.symbolSize.Width /4),
				(position.row * this.symbolSize.Height) + this.slotWindow.y - (this.symbolSize.Height /4),

				1 * this.symbolSize.Width + (this.symbolSize.Width /2),
				1 * this.symbolSize.Height + (this.symbolSize.Height/2)
			);
			let wildCoverRect = Geometry.CoverRect(this.sprite.CurrentFrameRect, wildContainRect, HorizontalAlign.Center, VerticalAlign.Center);
			let wildCoverInflatedRect = this.InflateWildSize(wildCoverRect);

			// avoid to go to second screen
			wildCoverInflatedRect = wildCoverInflatedRect.SetPosition(wildCoverInflatedRect.x, Math.max(wildCoverInflatedRect.y,0));
			
			// set new position and size of the wild
			wild.SetRect(wildSymbolsRect, centerSymbol, wildCoverInflatedRect);
		}
	}

	protected started: boolean = false;
	protected stopRequested: boolean = false;

	public get IsRunning(): boolean
	{
		return this.started;
	}

	public Start(): void 
	{
		this.started = true;
		this.stopRequested = false;

		this.currentWild = -1;
		this.nextWildDelay.Start();
		
		// start first wild right now
		this.NextWild();
	}

	public RequestStop(): void
	{
		if (this.started)
		{
			if (!this.stopRequested)
			{
				Log.Error(this.logGroup, "Requesting stop!");

				this.stopRequested = true;
				// stop timer if running
				this.nextWildDelay.Stop();

				this.sprite.Restart(false);

				// hide current wild
				if (this.currentWild != -1) 
				{
					this.wilds[this.currentWild].Hide(); // hide will also apply
					//this.wilds[this.currentWild].Apply();

					// make sure to redraw sprites
					this.wilds[this.currentWild].Update();
				}

				// go fast trough remaining wilds
				for (this.currentWild++; this.currentWild <this.lastResult.Positions.length; this.currentWild++)
				{
					// only apply wild without showing it
					this.wilds[this.currentWild].Apply();

					this.wilds[this.currentWild].Update();

					// move to next
					this.OnNext(this.currentWild, this.wilds[this.currentWild].symbolsRect);
				}

				this.StopAudio();

				// reset current wild
				this.currentWild = -1;

				this.started = false;
				this.stopRequested = false;

				this.OnComplete();
			}
			else
			{
				Log.Error(this.logGroup, "Stop already requested!");
			}
		}
		else
		{
			Log.Error(this.logGroup, "Feature not started!");
		}
	}

	protected InflateWildSize(wildRect: Rect): Rect
	{
		return wildRect.Inflate(new Size(wildRect.width * 0.1, wildRect.height * 0.1));
	}

	private OnWildShown(wildIndex: number) {
		this.sprite.Restart(false);
	}

	private NextWild(): void {

		if (this.currentWild >= this.lastResult.Positions.length -1) {

			// hide the last
			if (this.currentWild != -1) {
				this.wilds[this.currentWild].Hide();
			}
			
			// finished
			// Log.Debug(this.logGroup, "Wilds complete, shown " + this.lastResult.Positions.length + " wilds");

			this.nextWildDelay.Stop();
			this.started = false;
			this.stopRequested = false;
			this.OnComplete();
		}
		else {

			if (this.currentWild != -1) {
				// hide current wild
				this.wilds[this.currentWild].Hide();
			}

			this.currentWild++;

			// Log.Debug(this.logGroup, "Displaying wild " + this.currentWild + " of " + this.lastResult.Positions.length);


			this.wilds[this.currentWild].Show();

			// play audio - repeat up to 3 times depending on wild size
			let wildArea: number = this.wilds[this.currentWild].symbolsRect.Area();
			let repeats: number = 1;
			if (wildArea == 4) {
				// one time
			}
			else if (wildArea == 6) {
				repeats = 2;
			}
			else if (wildArea == 9) {
				repeats = 3;
			}

			// Log.Debug(this.logGroup, "Expanding wild area " + wildArea + ", repeats " + repeats);

			this.audio.Play(this.resid.ScopedResourceName("background"));

			this.OnNext(this.currentWild, this.wilds[this.currentWild].symbolsRect);
		}
	}

	protected StopAudio(): void
	{
		// then also stop audio
		this.audio.Stop(this.resid.ScopedResourceName("background"));
	}

	public Update() {
		if (this.currentWild != -1) {
			this.wilds[this.currentWild].Update();
		}
	}



	public View(): HTMLElement {
		return this.view;
	}

	private completeCallbaks: ExpandingWildsBulkFinishedCallback[] = [];
	private OnComplete() {
		for (let callback of this.completeCallbaks) {
			callback();
		}
	}

	public AddOnCompleteCallback(callback: ExpandingWildsBulkFinishedCallback) {
		this.completeCallbaks.push(callback);
	}

	private nextWildCallbacks: ExpandingWildsBulkNextWildCallback[] = [];
	private OnNext(wildIndex: number, rect: Rect) {
		for (let callback of this.nextWildCallbacks) {
			callback(wildIndex, rect);
		}
	}

	public AddOnNextCallback(callback: ExpandingWildsBulkNextWildCallback) {
		this.nextWildCallbacks.push(callback);
	}
}

export interface ExpandingWildsBulkParam
{
	readonly resid: ResourceIdentifier;
	readonly logGroup: LogGroup;
	readonly resourcesBasePath: string;
	readonly slotWindow: Rect;
	readonly slotWindowSymbolSize: PlayWindow;
	readonly reelSetMapper: ReelSetMapper;
	readonly audio: AudioManager;
	readonly gameName: string;
    readonly availableSymbols: SlotSymbol[];
	readonly systemContext: ISystemContext;
	readonly symbolTypeAnim : SymbolTypeAnim
}

export interface IExpandingWildsBulkFactory{
	CreateExpandingWildsBulk(param: ExpandingWildsBulkParam, config: ExpandingWildsBulkConfig): ExpandingWildsBulk;
	CreateExpandingWildsBulkPIXIAPP(param: ExpandingWildsBulkParam, config: ExpandingWildsBulkConfig): ExpandingWildsBulkPIXIAPP;
}

export class ExpandingWildsBulkFactory implements IExpandingWildsBulkFactory 
{
	public CreateExpandingWildsBulk(param: ExpandingWildsBulkParam, config: ExpandingWildsBulkConfig): ExpandingWildsBulk
	{ 
		return new ExpandingWildsBulk(param, config);
	}
	public CreateExpandingWildsBulkPIXIAPP(param: ExpandingWildsBulkParam, config: ExpandingWildsBulkConfig): ExpandingWildsBulkPIXIAPP
	{ 
		return new ExpandingWildsBulkPIXIAPP(param, config);
	}
}
